import * as Yup from "yup";
import {
  LOCATION_RADIUS_MAX,
  LOCATION_RADIUS_MIN,
  PARTICIPANT_MODE,
  TARGETING_TYPE,
  TARGETING_TYPE_OPTIONS,
} from "./locationManagementConstants";

// US: (###) ###-#### or ###-###-#### or ##########
const US_PHONE_REGEX = /^(\+1[-\s]?)?(\(?\d{3}\)?[-\s]?)\d{3}[-\s]?\d{4}$/;
// CA: same NANP format as US
const CA_PHONE_REGEX = US_PHONE_REGEX;
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,3}$/;
const US_ZIP_CODE_REGEX = /^\d{5}$/;
const CA_POSTAL_CODE_REGEX = /^[A-Z]\d[A-Z]\d[A-Z]\d$/i;

// Phone: allow digits + common formatting characters only
const PHONE_ALLOWED_CHARS_REGEX = /^[0-9()+\-\s]*$/;

type ParticipantMode = (typeof PARTICIPANT_MODE)[keyof typeof PARTICIPANT_MODE];

const phoneSchemaForCountry = (country?: string) => {
  const regex = country === "CA" ? CA_PHONE_REGEX : US_PHONE_REGEX;
  return Yup.string().test(
    "phone-format",
    "Phone number is either invalid or in the wrong format",
    (value) => !value || regex.test(value)
  );
};

const ZIP_LOCATION_NOT_FOUND_MESSAGE =
  "No data found for this ZIP code. Please enter a valid one.";

const zipLookupSchema = (fieldName: string) =>
  Yup.string().test(
    "zip-lookup",
    ZIP_LOCATION_NOT_FOUND_MESSAGE,
    function (value) {
      if (value) return true;
      if (!this.parent.zip_lookup_error) return true;

      return this.createError({
        path: fieldName,
        message: `No ${fieldName} found for this ZIP code. Please enter a valid one.`,
      });
    }
  );

const optionalDecimalSchema = (label, min, max) =>
  Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError(`${label} must be a number`)
    .min(min, `${label} must be greater than or equal to ${min}.`)
    .max(max, `${label} must be lower than or equal to ${max}.`)
    .nullable();

const emptyStringToNull = (value, originalValue) =>
  originalValue === "" ? null : value;

const hasValue = (value) => value !== "" && value != null;

export const buildLocationFormSchema = (
  existingEmails: string[] = [],
  isEditMode = false
) => {
  return Yup.object().shape({
    location_brand_id: Yup.string()
      .max(
        100,
        "Store/Location number (unique ID) must be at most 100 characters"
      )
      .notRequired(),
    location_name: Yup.string()
      .max(100, "Location Name must be at most 100 characters")
      .required("Location Name is required"),
    country: Yup.string().required("Country is required"),
    zip_code: Yup.string()
      .required("Zip Code is required")
      .when("country", {
        is: (c: string | string[]) => (Array.isArray(c) ? c[0] : c) === "US",
        then: (schema) =>
          schema.matches(
            US_ZIP_CODE_REGEX,
            "US ZIP codes must be exactly 5 digits"
          ),
        otherwise: (schema) =>
          schema.when("country", {
            is: (c: string | string[]) =>
              (Array.isArray(c) ? c[0] : c) === "CA",
            then: (s) =>
              s.matches(
                CA_POSTAL_CODE_REGEX,
                "Canadian postal codes must be exactly 6 characters"
              ),
            otherwise: (s) => s,
          }),
      }),
    zip_lookup_error: Yup.boolean(),
    state: zipLookupSchema("state")
      .min(2, "State must be at least 2 characters")
      .max(150, "State must be at most 150 characters")
      .required("No state found for this ZIP code. Please enter a valid one."),
    city: zipLookupSchema("city")
      .min(2, "City must be at least 2 characters")
      .max(150, "City must be at most 150 characters")
      .required("No city found for this ZIP code. Please enter a valid one."),
    location_street_address: Yup.string()
      .max(100, "Location Street Address must be at most 100 characters")
      .required("Location Street Address is required"),
    group_id: Yup.array()
      .min(1, "Groups are required")
      .required("Groups are required"),
    landing_page_url: Yup.string()
      .max(100, "Landing Page URL must be at most 100 characters")
      .matches(
        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/,
        "Invalid URL format"
      )
      .required("Landing Page URL is required"),
    targeting_type: Yup.string()
      .transform((value) => (value === "" ? undefined : value))
      .oneOf(
        TARGETING_TYPE_OPTIONS.map((option) => option.value),
        "Targeting Type must be radius, zip, or radius_and_zip"
      )
      .notRequired(),
    radius: Yup.number()
      .transform(emptyStringToNull)
      .typeError("Radius Targeting must be a number")
      .min(
        LOCATION_RADIUS_MIN,
        `This value should be greater than or equal to ${LOCATION_RADIUS_MIN}.`
      )
      .max(
        LOCATION_RADIUS_MAX,
        `This value should be lower than or equal to ${LOCATION_RADIUS_MAX}.`
      )
      .integer("Radius Targeting must be a whole number")
      .nullable()
      .when("targeting_type", {
        is: (targetingType) =>
          targetingType === TARGETING_TYPE.RADIUS ||
          targetingType === TARGETING_TYPE.RADIUS_AND_ZIP,
        then: (schema) => schema.required("Radius Targeting is required"),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "radius-empty-for-zip",
        "Radius Targeting must be empty when Targeting Type is Zip Codes only",
        function (value) {
          if (!this.parent.targeting_type) {
            return (
              !hasValue(value) ||
              this.createError({
                message:
                  "Radius Targeting must be empty when Targeting Type is blank",
              })
            );
          }
          if (
            this.parent.targeting_type &&
            this.parent.targeting_type !== TARGETING_TYPE.ZIP
          ) {
            return true;
          }
          return !hasValue(value);
        }
      ),
    geotarget: Yup.string()
      .max(100, "Zip Code Targeting must be at most 100 characters")
      .when("targeting_type", {
        is: (targetingType) =>
          targetingType === TARGETING_TYPE.ZIP ||
          targetingType === TARGETING_TYPE.RADIUS_AND_ZIP,
        then: (schema) => schema.required("Zip Code Targeting is required"),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "geotarget-empty-for-radius",
        "Zip Code Targeting must be empty when Targeting Type is radius only",
        function (value) {
          if (!this.parent.targeting_type) {
            return (
              !hasValue(value?.trim?.() ?? value) ||
              this.createError({
                message:
                  "Zip Code Targeting must be empty when Targeting Type is blank",
              })
            );
          }
          if (
            this.parent.targeting_type &&
            this.parent.targeting_type !== TARGETING_TYPE.RADIUS
          ) {
            return true;
          }
          return !hasValue(value?.trim?.() ?? value);
        }
      ),
    franchise_group: Yup.string()
      .max(100, "Franchise Group must not exceed 100 characters.")
      .notRequired(),
    location_lat: optionalDecimalSchema("Latitude", -90, 90),
    location_long: optionalDecimalSchema("Longitude", -180, 180),
    targeting_same_as_location: Yup.mixed()
      .oneOf([1, 0], "Targeting Same as Location must be 1 or 0")
      .notRequired(),
    targeting_latitude: optionalDecimalSchema("Targeting Latitude", -90, 90),
    targeting_longitude: optionalDecimalSchema(
      "Targeting Longitude",
      -180,
      180
    ),

    participant_mode: Yup.string()
      .oneOf([PARTICIPANT_MODE.EXISTING, PARTICIPANT_MODE.NEW])
      .required("Please select a participant assignment method"),

    existing_participant_id: Yup.string().when("participant_mode", {
      is: (mode: ParticipantMode) =>
        mode === PARTICIPANT_MODE.EXISTING && !isEditMode,
      then: (schema) => schema.required("Please select a participant"),
      otherwise: (schema) => schema.notRequired(),
    }),

    participant_first_name: Yup.string().when("participant_mode", {
      is: (mode: ParticipantMode) =>
        mode === PARTICIPANT_MODE.NEW || isEditMode,
      then: (schema) =>
        schema
          .max(100, "Participant first name must be at most 100 characters")
          .required("Participant first name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    participant_last_name: Yup.string().when("participant_mode", {
      is: (mode: ParticipantMode) =>
        mode === PARTICIPANT_MODE.NEW || isEditMode,
      then: (schema) =>
        schema
          .max(100, "Participant last name must be at most 100 characters")
          .required("Participant last name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    email_address: Yup.string()
      .matches(EMAIL_REGEX, "Invalid email address")
      .test("email-unique", "Email address already exists", (value) => {
        if (!value) return true;
        return !existingEmails.includes(value.toLowerCase());
      })
      .when("participant_mode", {
        is: (mode: ParticipantMode) =>
          mode === PARTICIPANT_MODE.NEW || isEditMode,
        then: (schema) => schema.required("Email Address is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    phone: Yup.string()
      .when("country", (country, _schema) =>
        phoneSchemaForCountry(Array.isArray(country) ? country[0] : country)
      )
      .matches(
        PHONE_ALLOWED_CHARS_REGEX,
        "Phone number cannot contain special characters"
      )
      .when("participant_mode", {
        is: (mode: ParticipantMode) =>
          mode === PARTICIPANT_MODE.NEW || isEditMode,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
};
